import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { localeHref, useT } from "@/lib/i18n";
import collCeiling from "@/assets/farve/coll-ceiling.jpg";
import collMushroom from "@/assets/farve/coll-mushroom.jpg";
import collWall from "@/assets/farve/coll-wall.jpg";
import heroOutlet from "@/assets/farve/hero-outlet.jpg";

export const Route = createFileRoute("/en/murano")({
  head: () => ({
    meta: [
      { title: "Murano selection — Farveladeland" },
      {
        name: "description",
        content:
          "Farveladeland's Murano selection: vintage hand-blown ceiling lamps, mushroom lamps and wall lamps from the island of Murano.",
      },
      { property: "og:title", content: "Murano selection — Farveladeland" },
      {
        property: "og:description",
        content: "Vintage Murano lamps and chandeliers, curated in Copenhagen.",
      },
      { property: "og:image", content: collCeiling },
      { property: "og:locale", content: "en_US" },
    ],
  }),
  component: MuranoPage,
});

function MuranoPage() {
  const location = useLocation();
  const { t, locale } = useT();
  const quizHref = localeHref(locale, "/murano/match");
  const currentPath = location.pathname.replace(/\/$/, "");

  if (currentPath === "/murano/match" || currentPath === "/en/murano/match") {
    return <Outlet />;
  }

  const editions = [
    {
      n: "01",
      title: t("murano.editions.01.title"),
      italian: t("murano.editions.01.italian"),
      image: collCeiling,
      text: t("murano.editions.01.text"),
      range: t("murano.editions.01.range"),
      handle: "murano-loftlamper",
    },
    {
      n: "02",
      title: t("murano.editions.02.title"),
      italian: t("murano.editions.02.italian"),
      image: collMushroom,
      text: t("murano.editions.02.text"),
      range: t("murano.editions.02.range"),
      handle: "murano-mushroom-lampe",
    },
    {
      n: "03",
      title: t("murano.editions.03.title"),
      italian: t("murano.editions.03.italian"),
      image: collWall,
      text: t("murano.editions.03.text"),
      range: t("murano.editions.03.range"),
      handle: "murano-vaeglamper",
    },
  ];

  const facts = [
    { k: t("murano.fact.1.k"), v: t("murano.fact.1.v") },
    { k: t("murano.fact.2.k"), v: t("murano.fact.2.v") },
    { k: t("murano.fact.3.k"), v: t("murano.fact.3.v") },
  ];

  return (
    <>
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-52 md:pb-32">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-7">
              <p className="eyebrow">{t("murano.eyebrow")}</p>
              <h1 className="mt-6 font-display text-[clamp(3rem,8vw,7rem)] leading-[0.95] tracking-tight">
                {t("murano.titleA")}
                <br />
                <em className="text-terracotta">{t("murano.titleB")}</em>
                <br />
                <span className="text-foreground/70">{t("murano.titleC")}</span>
              </h1>
            </div>
            <div className="flex flex-col justify-end gap-6 md:col-span-5">
              <p className="text-pretty text-base leading-relaxed text-muted-foreground md:text-lg">
                {t("murano.body")}
              </p>
              <a
                href={quizHref}
                className="group inline-flex w-fit items-center gap-3 rounded-full bg-foreground px-6 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-background transition-colors hover:bg-terracotta"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-background/70 transition-colors group-hover:bg-background" />
                {t("match.cta")} →
                <span className="ml-1 hidden text-[10px] tracking-[0.18em] text-background/60 sm:inline">
                  {t("match.ctaSub")}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 md:px-10">
        <div className="space-y-32 md:space-y-44">
          {editions.map((e, i) => (
            <article
              key={e.n}
              className={`grid gap-10 md:grid-cols-12 md:items-center md:gap-20 ${
                i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="md:col-span-7">
                <div className="aspect-[4/5] overflow-hidden rounded-sm bg-muted">
                  <img src={e.image} alt={e.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
              </div>
              <div className="md:col-span-5">
                <p className="font-mono text-xs text-terracotta">№ {e.n}</p>
                <p className="mt-4 eyebrow">{e.italian}</p>
                <h2 className="mt-3 font-display text-5xl leading-[1.05] md:text-6xl">{e.title}</h2>
                <p className="mt-6 text-base leading-relaxed text-muted-foreground">{e.text}</p>
                <div className="mt-8 flex items-center gap-6">
                  <Link
                    to="/collections/$handle"
                    params={{ handle: e.handle }}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-3 text-xs font-medium uppercase tracking-[0.2em] text-background hover:bg-terracotta"
                  >
                    {t("murano.shopCta")} →
                  </Link>
                  <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{e.range}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-32 max-w-[1500px] px-6 md:px-10">
        <div className="border-t border-border/60 pt-16 md:pt-24">
          <div className="md:flex md:items-end md:justify-between md:gap-12">
            <div className="max-w-2xl">
              <p className="eyebrow">{locale === "da" ? "Vidensbank" : "Knowledge"}</p>
              <h2 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl">
                {locale === "da" ? "Murano-leksikon" : "Murano encyclopedia"}
              </h2>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">
                {locale === "da"
                  ? "Vores samlede viden om vintage Murano-glas — teknikker, producenter, historie og pleje."
                  : "Everything we know about vintage Murano glass — techniques, makers, history and care."}
              </p>
            </div>
            <Link
              to="/murano/leksikon"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-foreground px-5 py-3 text-xs font-medium uppercase tracking-[0.2em] text-foreground transition hover:bg-foreground hover:text-background md:mt-0"
            >
              {locale === "da" ? "Åbn leksikonet" : "Open encyclopedia"} →
            </Link>
          </div>
        </div>
      </section>

      <section className="relative mt-32 overflow-hidden bg-foreground py-24 text-background md:py-32">
        <img src={heroOutlet} alt="" aria-hidden loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
          <div className="grid gap-12 md:grid-cols-3">
            {facts.map((s) => (
              <div key={s.k} className="border-t border-background/20 pt-6">
                <p className="font-display text-5xl text-background md:text-6xl">{s.k}</p>
                <p className="mt-4 max-w-xs text-sm leading-relaxed text-background/75">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
